import { useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { Box, Typography } from '@mui/material'
import { InnerHtml, Loading } from 'components/ui'
import { getGlobalConsentApi } from 'pages/policies/api'

import request from '../../../../api'
import { useMappedTranslation, usePageTitle } from '../../../../hooks'
import { convertCountryCode2to3, countryCode } from '../../../../utils/i18n'
import ConsentList from '../../consent-list'
import Detail from '../../views/detail'

type Consent = {
  root: boolean
  id: string
  title: string
  uri: string
}

export default function Operators() {
  const t = useMappedTranslation()
  const navigate = useNavigate()
  const { consentId } = useParams()

  const location = useLocation()
  const [searchParams] = useSearchParams()
  const paramCountryCode = searchParams.get('countryCode') || undefined
  const paramLocale = searchParams.get('locale') || undefined
  const effectiveCountryCode = paramCountryCode ? convertCountryCode2to3(paramCountryCode) : countryCode

  const [selected, setSelected] = useState<Consent | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [consents, setConsents] = useState<Consent[]>([])
  const [detail, setDetail] = useState<string>('')

  usePageTitle(consentId ? selected?.title : t('policy.operator.title'))

  const getConsent = () => {
    if (location.state?.consent) {
      setSelected(location.state?.consent)
      return Promise.resolve(location.state?.consent)
    }

    return getGlobalConsentApi('OPL', true, { overrides: { countryCode: paramCountryCode, locale: paramLocale } }).then((res) => {
      if (res.status !== 200) {
        throw new Error('Failed getConsentApi')
      } else {
        const data = res.data as Consent[]
        return data.find((item) => item.id.toString() === consentId)
      }
    })
  }

  useEffect(() => {
    setIsLoading(true)
    setSelected(null)
    setDetail('')

    if (effectiveCountryCode !== 'USA') {
      navigate('/')
    }

    if (consentId) {
      getConsent()
        .then(async (data) => {
          if (data?.uri === undefined) {
            throw new Error('Failed getConsentApi')
          }
          setSelected(data)

          return await request.get(data.uri).then((res) => {
            if (res.status !== 200) {
              throw new Error('Failed getConsentApi')
            }

            const dom = new DOMParser().parseFromString(res.data, 'text/html')
            const body = dom.getElementsByTagName('body').item(0)?.innerHTML
            if (body !== undefined) {
              setDetail(body)
            }
          })
        })
        .catch(() => {
          setDetail('<div>Failed to load</div>')
        })
        .finally(() => {
          setIsLoading(false)
        })
    } else {
      getGlobalConsentApi('OPL', true, { overrides: { countryCode: paramCountryCode, locale: paramLocale } })
        .then((res) => {
          if (res.status !== 200) {
            throw new Error('Failed getConsentApi')
          } else {
            const data = res.data as Consent[]
            setConsents(data)
          }
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
  }, [location, effectiveCountryCode, paramCountryCode, paramLocale])

  return (
    <Box sx={{ width: '100%', height: '100%', padding: { xs: '24px', md: '0' } }}>
      {isLoading ? (
        <Loading fullScreen />
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Box sx={{ width: { xs: '100%', md: '1040px' }, mb: '48px' }}>
            {consentId ? (
              <>{<Detail title={selected?.title || ''} isLoading={isLoading} detail={detail} />}</>
            ) : (
              <>
                <Box textAlign='center' paddingTop='36px'>
                  <Typography variant='h1' fontWeight='500' fontSize={{ xs: '36px', md: '48px' }}>
                    {t('policy.operator.title')}
                  </Typography>
                </Box>
                <Box sx={{ marginTop: '48px' }}>
                  <Typography>{t('policy.operator.description')}</Typography>
                </Box>
                <ConsentList list={consents} />
                <Box sx={{ marginTop: '60px', marginBottom: '48px' }}>
                  <Typography>
                    <InnerHtml>{t('policies.operator.question')}</InnerHtml>
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        </Box>
      )}
    </Box>
  )
}
